import type { Building } from "building-types/building/buildingTypes";
import type { Facade, GeneratedFacade } from "building-types/facades/facadeTypes";
import type { Label } from "building-types/facades/label";
import type { HalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { RWTable, Table } from "common/data/table";
import { componentInstanceChainFromId, ENTITY_ID_SEPARATOR } from "doc/entity/componentInstanceEntityResolve";
import { evalFormulas, evaluateEntityForComponentInstance, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  optionChoices,
  doc: { facades, optionSets, instanceOptionSets, roofs },
  labels,
  entityDisabledByGroup,
  enabledFeatures,
  labelEdgeBehaviors,
  idToParents,
  componentToTransforms,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    facades,
    roofs,
    labels,
    optionChoices: resolveOptionsInFormula(facades, definitions, optionChoices),
    entityDisabledByGroup,
    definitions,
    enabledFeatures,
    labelEdgeBehaviors,
    idToParents,
    componentToTransforms,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  definitions: "shallow",
  optionChoices: "shallow",
  entityDisabledByGroup: "shallow",
  enabledFeatures: "shallow",
  labelEdgeBehaviors: "shallow",
  idToParents: "shallow",
  componentToTransforms: "shallow",
});

function generate({
  facades,
  roofs,
  labels: inputLabels,
  optionChoices,
  entityDisabledByGroup,
  definitions,
  enabledFeatures,
  labelEdgeBehaviors,
  idToParents,
  componentToTransforms,
}: ReturnType<typeof select>): {
  labels: Building["labels"];
  facades: Building["facades"];
} {
  const labels: {
    [levelId: string]: HalfEdgeAssociationTable<Label>;
  } = { ...inputLabels };

  const evaluatedFacades = evalFormulas(facades, (v) => optionChoices[v], undefined, entityDisabledByGroup);
  const filteredFacades = removeDisabledFacades(evaluatedFacades);

  const instancedFacades: RWTable<GeneratedFacade> = {};

  if (enabledFeatures.modelComponents) {
    // Label edge behaviors are already expanded/evaluated per instance in generateEdgeBehaviors
    // (parentId remapped; id encodes the instance chain). Facade disable is still evaluated here
    // in each instance context.
    const tryAddInstanceFacade = (facadeId: string, componentInstanceChain: readonly string[] | undefined): void => {
      const instanceId = componentInstanceChain?.[0];
      if (!instanceId) {
        return;
      }

      const originalFacade = facades[facadeId];
      if (!originalFacade) {
        return;
      }

      const instanceFacadeId = `${facadeId}${ENTITY_ID_SEPARATOR}${instanceId}`;
      if (evaluatedFacades[instanceFacadeId] || instancedFacades[instanceFacadeId]) {
        return;
      }

      const componentFacade = evaluateEntityForComponentInstance(
        originalFacade,
        componentInstanceChain,
        definitions,
        optionChoices,
      );

      if (!componentFacade.disabled && !entityDisabledByGroup[componentFacade.id]) {
        instancedFacades[instanceFacadeId] = {
          ...componentFacade,
          id: instanceFacadeId,
          sourceId: facadeId,
          componentInstanceChain,
        };
      }
    };

    for (const behaviorId in labelEdgeBehaviors) {
      const behavior = labelEdgeBehaviors[behaviorId];
      const componentInstanceChain = componentInstanceChainFromId(behavior.id);
      if (componentInstanceChain) {
        tryAddInstanceFacade(behavior.facadeId, componentInstanceChain);
      }
    }

    // Roof-wall path: facades are attached via gableEndLabels rather than edge behaviors,
    // so we walk the roof->component->instance chain explicitly.
    for (const roofId in roofs) {
      const roof = roofs[roofId];
      if (!roof?.gableEndLabels?.length) {
        continue;
      }
      const componentIds = idToParents[roofId] || [];
      for (const componentId of componentIds) {
        const transforms = componentToTransforms[componentId] || [];
        for (const transform of transforms) {
          for (const { facadeId } of roof.gableEndLabels) {
            tryAddInstanceFacade(facadeId, transform.componentInstanceChain);
          }
        }
      }
    }
  }

  return { labels, facades: { ...filteredFacades, ...instancedFacades } };
}

function removeDisabledFacades(facades: Table<Facade>): Table<GeneratedFacade> {
  const filtered: RWTable<GeneratedFacade> = {};

  for (const facadeId in facades) {
    const facade = facades[facadeId];
    if (!facade) {
      continue;
    }

    if (facade.disabled) {
      continue;
    }

    filtered[facadeId] = facade;
  }

  return filtered;
}

export default memo(select, generate, compare);
